import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import React, { useRef, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import {
  ArrowDown,
  CalendarDays,
  Check,
  CircleCheck,
  Clock,
  Copy,
  Crown,
  FileText,
  Gift,
  LucideIcon,
  MessageSquare,
  RefreshCw,
  Smile,
  Target,
  TrendingUp,
  User,
  Zap,
} from "lucide-react";
import { copyCodeToClipboard } from "utils/misc";
import { hexToRGB } from "@shared/utils/misc";
import cornixHorizontalLogo from "images/svg_logo_horizontal.svg";
import cornixWhiteHorizontalLogo from "images/svg_white_logo_horizontal.svg";
import { IconTile } from "common/NewLook";
import { Role } from "features/whop/whopLanding/WhopLandingPage.tsx";
import {
  benefitsContent,
  finalCtaContent,
  heroContent,
} from "features/whop/whopLanding/content.ts";
import { memberMessage } from "features/whop/whopLanding/components/MessageSection.tsx";
import { DemoFormSection } from "features/whop/whopLanding/components/DemoFormSection.tsx";
import {
  ScrollReveal,
  fadeUpAnimation,
} from "features/whop/whopLanding/components/animations.tsx";

const YOUTUBE_EMBED_LINK = "https://www.youtube.com/embed/s-TGuPticPw?rel=0";
const CALENDLY_LINK =
  "https://calendly.com/cornix-trading/cornix-channel-onboarding";

const BENEFIT_ICONS: Record<string, LucideIcon> = {
  zap: Zap,
  clock: Clock,
  target: Target,
  refresh: RefreshCw,
  clock8: Clock,
  gift: Gift,
  smile: Smile,
  trending: TrendingUp,
  message: MessageSquare,
  file: FileText,
};

const ROLES: { role: Role; label: string; icon: LucideIcon }[] = [
  { role: "member", label: "I'm a Group Member", icon: User },
  { role: "admin", label: "I Run a Trading Group", icon: Crown },
];

const openCalendly = () =>
  window.open(CALENDLY_LINK, "_blank", "noopener,noreferrer");

/** Copies the message for the admin and flips its button to "Copied". */
const useCopyMessage = () => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    copyCodeToClipboard(memberMessage).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return { copied, copy };
};

/**
 * The new look's Whop landing: the cornix.io language (a quiet brand bar, a
 * two-column hero with the role switch up front, bordered benefit cards, the
 * admin message beside its copy action, and a navy closing panel), on the
 * app's tokens so it follows light and dark.
 */
const NewLookWhopLanding = ({
  role,
  setRole,
}: {
  role: Role;
  setRole: (role: Role) => void;
}) => {
  const { isDarkMode } = useTheme();
  const messageRef = useRef<HTMLDivElement>(null);
  const hero = heroContent[role];
  const benefits = benefitsContent[role];
  const closing = finalCtaContent[role];
  const messageCopy = useCopyMessage();
  const closingCopy = useCopyMessage();

  return (
    <Page>
      <TopBar>
        <Logo
          src={isDarkMode ? cornixWhiteHorizontalLogo : cornixHorizontalLogo}
          alt="Cornix"
        />
      </TopBar>

      <Hero>
        <HeroText $delay={0}>
          <RoleSwitch role="group" aria-label="Select your role">
            {ROLES.map(({ role: value, label, icon: Icon }) => (
              <RoleButton
                key={value}
                type="button"
                $active={role === value}
                aria-pressed={role === value}
                onClick={() => setRole(value)}
              >
                <Icon size={16} strokeWidth={1.75} />
                {label}
              </RoleButton>
            ))}
          </RoleSwitch>
          <Headline>{hero.headline}</Headline>
          <Subheadline>{hero.subheadline}</Subheadline>
          <FreeNote>
            <CircleCheck size={16} strokeWidth={1.75} />
            {hero.support}
          </FreeNote>
          <HeroActions>
            {role === "admin" ? (
              <PrimaryButton type="button" onClick={openCalendly}>
                <CalendarDays size={18} strokeWidth={1.75} />
                {hero.secondaryCTA}
              </PrimaryButton>
            ) : (
              <PrimaryButton
                type="button"
                onClick={() =>
                  messageRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  })
                }
              >
                <ArrowDown size={18} strokeWidth={1.75} />
                {hero.primaryCTA}
              </PrimaryButton>
            )}
          </HeroActions>
        </HeroText>
        <HeroVideo $delay={160}>
          <VideoFrame>
            <iframe
              src={YOUTUBE_EMBED_LINK}
              title="Cornix for Whop"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </VideoFrame>
        </HeroVideo>
      </Hero>

      <Band>
        <Section>
          <ScrollReveal>
            <SectionTitle>{benefits.headline}</SectionTitle>
          </ScrollReveal>
          <BenefitGrid>
            {benefits.benefits.map((benefit, index) => (
              <ScrollReveal
                key={benefit.title}
                delay={index * 60}
                style={{ height: "100%" }}
              >
                <BenefitCard>
                  <IconTile
                    icon={BENEFIT_ICONS[benefit.icon] ?? Zap}
                    size={40}
                  />
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </BenefitCard>
              </ScrollReveal>
            ))}
          </BenefitGrid>
        </Section>
      </Band>

      {role === "admin" && (
        <DemoForm>
          <DemoFormSection />
        </DemoForm>
      )}

      {role === "member" && (
        <Section ref={messageRef}>
          <ScrollReveal>
            <MessageLayout>
              <MessageTitle $align="left">
                Copy This Message to Your Admin
              </MessageTitle>
              <MessageAction>
                <PrimaryButton type="button" onClick={messageCopy.copy}>
                  {messageCopy.copied ? (
                    <Check size={18} strokeWidth={1.75} />
                  ) : (
                    <Copy size={18} strokeWidth={1.75} />
                  )}
                  {messageCopy.copied ? "Copied to Clipboard!" : "Copy Message"}
                </PrimaryButton>
              </MessageAction>
              <MessageCard>
                <MessageBar aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </MessageBar>
                <MessageText>{memberMessage}</MessageText>
              </MessageCard>
            </MessageLayout>
          </ScrollReveal>
        </Section>
      )}

      <Section $tight={true}>
        <ScrollReveal>
          <Closing>
            <ClosingTitle>{closing.headline}</ClosingTitle>
            <ClosingText>{closing.subheadline}</ClosingText>
            <ClosingActions>
              {closing.primaryCTA && (
                <LightButton type="button" onClick={closingCopy.copy}>
                  {closingCopy.copied ? (
                    <Check size={18} strokeWidth={1.75} />
                  ) : (
                    <Copy size={18} strokeWidth={1.75} />
                  )}
                  {closingCopy.copied
                    ? "Copied to Clipboard!"
                    : closing.primaryCTA}
                </LightButton>
              )}
              {closing.secondaryCTA && (
                <LightButton type="button" onClick={openCalendly}>
                  <CalendarDays size={18} strokeWidth={1.75} />
                  {closing.secondaryCTA}
                </LightButton>
              )}
            </ClosingActions>
            <ClosingNote>
              Join 100+ trading groups already using automated execution
            </ClosingNote>
          </Closing>
        </ScrollReveal>
      </Section>
    </Page>
  );
};

export default NewLookWhopLanding;

const MAX_WIDTH = 1160;

const Page = styled.div`
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${(p) => p.theme.colors.layoutBgColor};
  color: ${(p) => p.theme.colors.primaryText};
  isolation: isolate;
`;
const TopBar = styled.header`
  display: flex;
  align-items: center;
  max-width: ${MAX_WIDTH + 56}px;
  margin: 0 auto;
  padding: 20px 28px;
  @media (max-width: 600px) {
    padding: 16px;
  }
`;
const Logo = styled.img`
  height: 28px;
  width: auto;
`;
const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  align-items: center;
  gap: 56px;
  max-width: ${MAX_WIDTH + 56}px;
  margin: 0 auto;
  padding: 40px 28px 80px;
  @media (max-width: 1080px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 40px;
    padding: 24px 28px 64px;
  }
  @media (max-width: 600px) {
    gap: 32px;
    padding: 8px 16px 48px;
  }
`;
const HeroText = styled.div<{ $delay: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${(p) => fadeUpAnimation(p.$delay)}
`;
const HeroVideo = styled.div<{ $delay: number }>`
  min-width: 0;
  ${(p) => fadeUpAnimation(p.$delay)}
`;
const RoleSwitch = styled.div`
  display: inline-flex;
  gap: 4px;
  max-width: 100%;
  padding: 4px;
  margin-bottom: 28px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border: ${(p) => p.theme.borders.card};
  @media (max-width: 600px) {
    display: flex;
    width: 100%;
    margin-bottom: 24px;
  }
`;
const RoleButton = styled.button<{ $active: boolean }>`
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  font: inherit;
  font-size: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms, color 150ms, box-shadow 150ms;
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$active &&
    css`
      background: ${p.theme.colors.cardBgColor};
      color: ${p.theme.colors.titleColor};
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      box-shadow: 0 1px 2px rgba(13, 20, 78, 0.08),
        0 2px 8px rgba(13, 20, 78, 0.06);
      svg {
        color: ${p.theme.colors.primaryBlue};
      }
    `}
  @media (max-width: 600px) {
    padding: 0 10px;
    font-size: 13px;
  }
`;
const Headline = styled.h1`
  margin: 0 0 20px;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: clamp(2.1rem, 1.2rem + 2.3vw, 3.25rem);
  line-height: 1.08;
  font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  letter-spacing: -0.025em;
  text-wrap: balance;
`;
const Subheadline = styled.p`
  margin: 0 0 20px;
  max-width: 560px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: clamp(1rem, 0.9rem + 0.4vw, 1.2rem);
  line-height: 1.6;
`;
const FreeNote = styled.p`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 32px;
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 14px;
  line-height: 1.5;
  svg {
    flex: none;
    margin-top: 2px;
    color: ${(p) => p.theme.colors.green};
  }
`;
const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  @media (max-width: 600px) {
    width: 100%;
    flex-direction: column;
  }
`;
const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 0 24px;
  border-radius: ${(p) => p.theme.radii.control}px;
  font: inherit;
  font-size: 15px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms, box-shadow 150ms, color 150ms,
    transform 150ms;
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 2px;
  }
  &:active {
    transform: translateY(1px);
  }
`;
const PrimaryButton = styled.button`
  ${buttonBase};
  border: 0;
  background: ${(p) => p.theme.colors.primaryBlue};
  color: ${(p) => p.theme.colors.white};
  box-shadow: 0 6px 18px ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.24)};
  &:hover {
    box-shadow: 0 8px 24px ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.32)};
    filter: brightness(1.05);
  }
`;
const VideoFrame = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 12px;
  border: ${(p) => p.theme.borders.card};
  background: ${(p) => p.theme.colors.cardLightBgColor};
  box-shadow: ${(p) => p.theme.shadows.card};
  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
const Band = styled.div`
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border-block: ${(p) => p.theme.borders.card};
`;
const Section = styled.section<{ $tight?: boolean }>`
  max-width: ${MAX_WIDTH + 56}px;
  margin: 0 auto;
  padding: 88px 28px;
  @media (max-width: 768px) {
    padding: 56px 20px;
  }
  @media (max-width: 480px) {
    padding: 44px 16px;
  }
  /* Follows another plain section: don't double the gap. */
  ${(p) =>
    p.$tight &&
    css`
      padding-top: 0 !important;
    `}
`;
const SectionTitle = styled.h2<{ $align?: "left" | "center" }>`
  margin: 0 auto 40px;
  max-width: 860px;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: clamp(1.7rem, 1rem + 1.8vw, 2.6rem);
  line-height: 1.15;
  font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  letter-spacing: -0.02em;
  text-wrap: balance;
  text-align: ${(p) => p.$align ?? "center"};
  ${(p) =>
    p.$align === "left" &&
    css`
      margin: 0 0 24px;
    `}
`;
const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;
const BenefitCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  padding: 24px;
  border-radius: ${(p) => p.theme.radii.card}px;
  border: ${(p) => p.theme.borders.panel};
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) => p.theme.shadows.panel};
  > :first-child {
    margin-bottom: 6px;
  }
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    column-gap: 14px;
    row-gap: 4px;
    padding: 18px;
    > :first-child {
      grid-row: span 2;
      margin: 0;
    }
  }
`;
const BenefitTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 17px;
  line-height: 1.35;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  @media (max-width: 600px) {
    font-size: 15px;
  }
`;
const BenefitText = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 14.5px;
  line-height: 1.6;
  @media (max-width: 600px) {
    font-size: 14px;
  }
`;
const MessageLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  grid-template-areas:
    ". card"
    "title card"
    "action card"
    ". card";
  grid-template-rows: 1fr auto auto 1fr;
  column-gap: 56px;
  row-gap: 24px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "title" "card" "action";
    grid-template-rows: none;
  }
`;
const MessageTitle = styled(SectionTitle)`
  grid-area: title;
  margin: 0;
  @media (max-width: 900px) {
    text-align: center;
  }
`;
const MessageAction = styled.div`
  grid-area: action;
  @media (max-width: 900px) {
    display: flex;
    justify-content: center;
    button {
      width: 100%;
      max-width: 360px;
    }
  }
`;
const MessageCard = styled.div`
  grid-area: card;
  overflow: hidden;
  border-radius: 12px;
  border: ${(p) => p.theme.borders.panel};
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) => p.theme.shadows.panel};
`;
const MessageBar = styled.div`
  display: flex;
  gap: 6px;
  padding: 14px 20px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  background: ${(p) => p.theme.colors.cardLightBgColor};
  span {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.lightBorderGrey};
  }
`;
const MessageText = styled.p`
  margin: 0;
  padding: 24px 28px 28px;
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 15px;
  line-height: 1.7;
  white-space: pre-line;
  overflow-wrap: anywhere;
  @media (max-width: 600px) {
    padding: 16px 20px 20px;
    font-size: 14px;
  }
`;
const DemoForm = styled.div`
  /* The admin demo form keeps its fields; the new look evens out the chrome. */
  > div {
    background: ${(p) => p.theme.colors.layoutBgColor};
    border: none;
  }
  h2 {
    letter-spacing: -0.02em;
    color: ${(p) => p.theme.colors.titleColor};
    font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  }
  p {
    color: ${(p) => p.theme.colors.secondaryText};
  }
  label {
    color: ${(p) => p.theme.colors.titleColor};
    font-size: 13px;
    font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  }
  input,
  select,
  textarea {
    border: ${(p) => p.theme.borders.card};
    border-radius: 8px;
    background: ${(p) => p.theme.colors.cardBgColor};
    color: ${(p) => p.theme.colors.primaryText};
    font-size: 14px;
    &:hover {
      border-color: ${(p) => p.theme.colors.lightBorderGrey};
    }
    &:focus {
      border-color: ${(p) => p.theme.colors.primaryBlue};
      box-shadow: 0 0 0 3px ${(p) => p.theme.colors.lightBlue};
    }
  }
  button[type="submit"] {
    ${buttonBase};
    background: ${(p) => p.theme.colors.primaryBlue};
    color: ${(p) => p.theme.colors.white};
    letter-spacing: 0;
  }
`;
const Closing = styled.div`
  position: relative;
  overflow: hidden;
  padding: 64px 40px;
  border-radius: 12px;
  text-align: center;
  background: ${(p) =>
    p.theme.isDarkMode
      ? p.theme.colors.cardLightBgColor
      : p.theme.colors.titleColor};
  border: ${(p) => (p.theme.isDarkMode ? p.theme.borders.card : "none")};
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 60% 70% at 50% 0%,
      ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.45)} 0%,
      transparent 70%
    );
    pointer-events: none;
  }
  > * {
    position: relative;
  }
  @media (max-width: 600px) {
    padding: 44px 20px;
    border-radius: 12px;
  }
`;
const ClosingTitle = styled.h2`
  margin: 0 auto 12px;
  max-width: 720px;
  color: ${(p) => p.theme.colors.white};
  font-size: clamp(1.7rem, 1rem + 2vw, 2.75rem);
  line-height: 1.15;
  font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  letter-spacing: -0.02em;
  text-wrap: balance;
`;
const ClosingText = styled.p`
  margin: 0 auto 32px;
  color: rgba(255, 255, 255, 0.75);
  font-size: clamp(1rem, 0.9rem + 0.4vw, 1.2rem);
`;
const ClosingActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;
// The closing panel is always dark, so its button is always white.
const LightButton = styled.button`
  ${buttonBase};
  border: 0;
  background: ${(p) => p.theme.colors.white};
  color: ${(p) => p.theme.colors.primaryBlue};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  &:hover {
    filter: brightness(0.96);
  }
`;
const ClosingNote = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13.5px;
`;
